import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import type { BillingPlan } from '../../lib/api'
import { formatPlanLimit, NEXT_PLAN, PLAN_LABEL, PLATFORM_FEE_PERCENT, SITE_PLAN_LIMITS } from '../../lib/planLimits'
import { lowestMonthlyPrice } from '../../lib/planPricing'
import { IconX } from '@tabler/icons-react'
import { UpsellCard } from './FeatureUpsell'
import { useI18n } from '../../lib/i18n'

/**
 * Lo que se promete del plan al que se sube — en CLAVES, no en texto.
 *
 * Vive en una constante de módulo, y una constante no puede llamar a un hook:
 * con el texto escrito aquí, la ventana seguiría en español en un panel en
 * inglés y nada lo diría. Quien las pinta sí es un componente y las traduce.
 */
const UPSELL: Record<'pro' | 'business', { pointKeys: string[] }> = {
  pro: {
    pointKeys: ['planLimit.pro.point1', 'planLimit.pro.point2', 'planLimit.pro.point3', 'planLimit.pro.point4'],
  },
  business: {
    pointKeys: ['planLimit.business.point1', 'planLimit.business.point2', 'planLimit.business.point3', 'planLimit.business.point4'],
  },
}

const CONTACT_EMAIL = 'info@veltica.com'

/**
 * Centered upsell shown when a create action is blocked by the plan limit.
 * Sells the next plan (free→Pro→Business); on Business it becomes "contact us".
 */
export function PlanLimitModal({ resource, kind, currentPlan, onClose, onOpenPlans }: {
  resource: string
  /**
   * Qué tope saltó, cuando cambia lo que hay que ofrecer. El de ofertas es el
   * que cambia todo el discurso; el del blog añade a la lista la nueva cantidad
   * de entradas publicadas que desbloquea el plan siguiente.
   */
  kind?: 'offers' | 'blogPosts'
  currentPlan: BillingPlan
  onClose: () => void
  onOpenPlans: () => void
}) {
  const { t } = useI18n()
  const next = NEXT_PLAN[currentPlan]

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const isContact = next === null
  const upsell = next && next !== 'free' ? UPSELL[next] : null
  /* Las dos palancas del plan siguiente, juntas y con sus cifras de verdad:
     cuántas ofertas caben y cuánta comisión se deja. Salen de los mismos topes
     que aplica el servidor. */
  const offersPitch = kind === 'offers' && next && next !== 'free'
    ? {
      lead: t(SITE_PLAN_LIMITS[next].offers === null ? 'planLimit.offers.leadUnlimited' : 'planLimit.offers.lead', {
        plan: PLAN_LABEL[next],
        count: formatPlanLimit(SITE_PLAN_LIMITS[next].offers),
        fee: PLATFORM_FEE_PERCENT[next],
        currentFee: PLATFORM_FEE_PERCENT[currentPlan],
      }),
      points: [
        SITE_PLAN_LIMITS[next].offers === null
          ? t('plans.limit.unlimitedF')
          : t('plans.limit.offers', { count: formatPlanLimit(SITE_PLAN_LIMITS[next].offers) }),
        t('planLimit.offers.fee', { fee: PLATFORM_FEE_PERCENT[next], currentFee: PLATFORM_FEE_PERCENT[currentPlan] }),
      ],
    }
    : null
  const blogPoint = kind === 'blogPosts' && next && next !== 'free'
    ? t(SITE_PLAN_LIMITS[next].blogPosts === null ? 'planLimit.blogPosts.unlimited' : 'planLimit.blogPosts.point', {
      count: formatPlanLimit(SITE_PLAN_LIMITS[next].blogPosts),
    })
    : null

  /* En `body` y no donde se monta: dentro de la tabla de Ofertas —o de
     cualquier contenedor con `transform`— el `position: fixed` del fondo se
     quedaba atrapado en ese contenedor y la ventana salía dentro de la tabla. */
  return createPortal(
    <div className="dash-limit-modal-backdrop" role="presentation" onClick={onClose}>
      {/*
        La MISMA tarjeta que vende una función, aquí vendiendo sitio.
        Era una ventana blanca con su propia maqueta, y en el asistente convivía
        a dos pantallas de distancia con la tarjeta de la mascota: dos caras
        para la misma frase —«esto entra en otro plan»— y ninguna de las dos se
        reconocía al volver a verla. Lo que cambia no es el diseño, son los
        textos, que aquí dependen del plan de quien mira y por eso viajan ya
        escritos.
      */}
      <div className="dash-limit-modal-shell" role="dialog" aria-modal="true" aria-label={t('planLimit.dialogLabel')} onClick={(e) => e.stopPropagation()}>
        <button className="dash-limit-modal-close" type="button" aria-label={t('planLimit.close')} onClick={onClose}>
          <IconX size={16} />
        </button>

        <UpsellCard
          standalone
          /* «LÍMITE ALCANZADO» y no «planes de pago»: es la razón por la que
             esta ventana se abrió sola, y es lo primero que hay que reconocer. */
          eyebrow={t('planLimit.eyebrow')}
          title={t('planLimit.title', { resource, plan: PLAN_LABEL[currentPlan] })}
          description={isContact
            ? t('planLimit.topPlan', { plan: PLAN_LABEL.business })
            : offersPitch?.lead ?? t('planLimit.upgradeLead', { plan: PLAN_LABEL[next], resource })}
          points={offersPitch
            ? [...offersPitch.points, ...(upsell ? upsell.pointKeys.slice(0, 2).map((clave) => t(clave)) : [])]
            : upsell ? [...(blogPoint ? [blogPoint] : []), ...upsell.pointKeys.map((clave) => t(clave))] : []}
          cta={isContact
            ? t('planLimit.contact')
            : t('planLimit.upgradeCta', { plan: PLAN_LABEL[next] })}
          /* El precio del plan que se ofrece, donde la tarjeta pone su letra
             pequeña. En el más alto no hay precio que dar: hay un correo. */
          note={next && next !== 'free' ? t('planLimit.price', { price: lowestMonthlyPrice(next) }) : t('planLimit.contactNote')}
          onOpenPlans={() => {
            if (isContact) {
              window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(t('planLimit.contactSubject'))}`
              return
            }
            onClose()
            onOpenPlans()
          }}
        />

        {/* Fuera de la tarjeta: cerrar sin subir de plan no es parte de la
            oferta, es la salida. */}
        <button className="dash-limit-modal-dismiss" type="button" onClick={onClose}>{t('planLimit.notNow')}</button>
      </div>
    </div>,
    document.body,
  )
}
